import { analytics } from './analytics.js';
import { announcementSeen, forgetNudgesSeenThisPage, markAnnouncementSeen, markNudgesSeen, nudgesAlreadySeen, nudgesOptedOut, optOutOfNudges, resetNudgesSeen, showNudges, visitorWrote } from './visit-marks.js';
import { PANEL_EDGE_OFFSET, applySide, withSafeArea } from './chrome.js';
import { endPageWait, reportPageMessageShown, requestPageCoverage } from './page-coverage.js';
import { buildPageKey, currentPage, isMobileViewport, mobileViewportQuery, onPathChange, pageKeyMatches } from './page.js';
import { optOutDoneMs, optOutOfferMs, optOutUndoMs, pageCoverageDelayMs, pageCoverageRecheckMs } from './constants.js';
import { origin, virtualPage } from './snippet.js';
import { resolveNudgeOptions } from './resolve-nudges.js';
import { runtime } from './runtime.js';
import { logBubble } from './stage-log.js';
import { urlRulesAllow } from './url-rules.js';
import { audience as visitorAudience } from './snippet.js';
import { xMarkIconSvg } from './leadwin-agent.js';

/**
 * The proactive corner: the bubbles the widget offers on its own, the page question written for the
 * route the visitor is on, and the campaigns a contractor scheduled. Fourth of init()'s surfaces.
 *
 * One factory rather than two, deliberately. Campaigns and bubbles are not two features sharing a
 * file, they are two claimants on one place — the corner beside the launcher. A campaign renders into
 * the bubble stack and retires the bubbles to take the slot; a bubble asks whether a campaign is
 * standing there before it appears. proactiveSlotFree() is that rule, and it only exists once. Split
 * along the obvious seam and the coupling does not go away, it just turns into an interface.
 * @param {object} ctx - The instance context (see init)
 * @returns {object} What the rest of the instance schedules, hooks and asks
 */
export function createProactiveSurfaces(ctx) {
    const { state, options, panel, button, trust, setOpen, openScreen, effectiveTheme } = ctx;

    /* ------------------------------------------------------------------------------------------------
     * Proactive nudges. The stack is built lazily when the first bubble is due, torn down on dismiss,
     * on an open and on a click, and never shown twice per tab. Every text is set through textContent —
     * it is contractor-authored copy, but it travels through a network payload, and this file runs on
     * someone else's website.
     * ---------------------------------------------------------------------------------------------- */
    state.nudgeStack = null;

    /* A round the corner still owes: the visitor took their opt-out back from inside the open chat, and
     * the bubbles cannot be offered over it. On state rather than in this closure because the panel is
     * what redeems it — see scheduleNudgesAfterClose and the panel's own close path. */
    state.nudgesOwedAfterClose = false;

    /* Watches the stack's own height so the trust cards keep clear of it whenever it changes after the
     * fact (see createNudgeStack). Disconnected with the stack it belongs to. */
    let nudgeStackResize = null;
    const nudgeTimers = [];

    /* Whether the bubble on screen is a page-specific question. A page question must not outlive its
     * page: unlike the generic nudge it is retired on the next SPA route change. */
    let pageBubbleShown = false;

    /* Whether a campaign card sits in the stack. It matched the page the visitor was on, so like the
     * page question it is retired on the next SPA route change — and its close is a plain dismiss: the
     * opt-out offer speaks about the chat's bubbles, not about the contractor's campaigns. */
    let announcementOnScreen = false;

    /* One interruption per page view: a modal that was shown suppresses every further modal (and, by
     * taking the slot, every bubble) until the next route change or page load resets the view. */
    let modalShownThisView = false;

    /* The open modal's backdrop, so a theme change repaints it and a chat open takes it away. */
    state.announcementBackdrop = null;

    /* The pending coverage report, so a visitor clicking through four routes in a second reports the
     * page they stop on rather than each one they passed through. */
    let pageCoverageTimer = null;

    /**
     * The page message for the page the visitor is on right now, or null. The page is the HOST and the
     * path: a provider may run the same widget on several domains, and "/kontakt" over there is not the
     * "/kontakt" being read here.
     */
    /**
     * This page's question for one audience exactly, or null.
     * @param {string} audience - '' for the default audience, 'provider' for a signed-in provider
     */
    function findPageMessage(audience) {
        const path = buildPageKey(runtime.pageQueryKeys);
        const host = currentPage().host.toLowerCase();

        for (const message of options.pageMessages) {
            const matches = message.folded ? pageKeyMatches(message.path, path) : message.path === path;
            if (matches && message.audience === audience
                && (message.host === '' || message.host === host)) {
                return message;
            }
        }

        return null;
    }

    /**
     * This page's question written FOR the visitor reading it, or null. Both what decides whether the page
     * is still owed a question and what the corner shows: a provider standing on a page that only carries
     * the client's question is standing on an uncovered page, and gets the site-wide bubble or none there,
     * never the client's question. That one asks "find a provider for your project?", and a provider who
     * accepted it started a conversation about something they never wanted (observed in production,
     * 29.09.2026). The '' audience is everyone who declared nothing provider-like, anonymous visitors and
     * signed-in buyers alike (see snippet.js).
     */
    function ownPageMessage() {
        return findPageMessage(visitorAudience);
    }

    /**
     * Reports the page the visitor is on for automatic coverage, unless there is nothing to gain. Two
     * guards, and both are what keeps this from being a second request on every page load of every
     * website: a widget that covers no pages never asks, and a page that already carries its question
     * never asks again — which is why a covered page costs nothing on its millionth view.
     * Runs on the initial page load and on every client-side route change, because to this feature the
     * two are the same event: a visitor arrived on a page.
     */
    function requestPageCoverageIfMissing() {
        // A stage page standing in for one of the provider's pages never reports: the text it would send
        // is OUR page's, and filed under his address it would become the question of his real page. The
        // corner does not wait either, so the site-wide bubble stands where no question is on file.
        // The visitor's OWN question, not any question: a provider on a page that carries only the
        // client's question is still owed theirs, and this is the request that gets it written.
        if (virtualPage || !options.pageCoverage || ownPageMessage() || !bubblesCanShowHere()) {
            return;
        }

        // The corner starts waiting HERE, not when the report goes out: between the two lie the grace
        // period below and a request, and a bubble scheduled shorter than that would offer the
        // site-wide sentence on a page whose own question is on its way. Every path out of the report
        // ends the waiting again (see requestPageCoverage and pollPageMessage).
        runtime.waitingForPath = buildPageKey(runtime.pageQueryKeys);

        // Deferred as a whole, guard included: after the delay the visitor may be on another route
        // entirely, and the question of whether THIS page needs covering has to be asked about the page
        // that is then on screen — the same page the extract is read from a line later.
        if (pageCoverageTimer) {
            clearTimeout(pageCoverageTimer);
        }

        pageCoverageTimer = setTimeout(function () {
            pageCoverageTimer = null;

            // Re-asked after the wait like the guard above, and for the reason the route handler names:
            // an instance whose launcher left the document must not report the page it no longer
            // belongs to.
            if (button.isConnected && options.pageCoverage && !ownPageMessage() && bubblesCanShowHere()) {
                requestPageCoverage();
                return;
            }

            endPageWait(buildPageKey(runtime.pageQueryKeys));
        }, pageCoverageDelayMs);
    }

    /**
     * Whether this campaign is still owed to the visitor on the page they are on right now: unseen under
     * its own frequency, and allowed on this page by its URL rules — no rules mean every page. Asked when the
     * campaign's own timer fires, so a visitor who navigated in the meantime is measured against the
     * page they are reading, not the one they arrived on.
     * @param {object} announcement - One validated campaign from the config payload
     * @returns {boolean}
     */
    function announcementDue(announcement) {
        if (announcementSeen(announcement)) {
            return false;
        }

        return urlRulesAllow(announcement.urlRules);
    }

    /**
     * The way a question LEAVES the chat on SPA navigation: the id is null, which clears a question the
     * visitor never answered from a chat that is still pristine, and hands the frame the page they are
     * reading now. Deliberately not the way a clicked bubble ARRIVES (see postBubbleToFrame): the page only
     * applies this while the chat is pristine, and it carries no answer and no generic bubble.
     */
    function postPageLeftToFrame() {
        if (!state.iframe || !state.iframe.contentWindow) {
            return;
        }

        try {
            state.iframe.contentWindow.postMessage({
                type: 'feedbax:widget:pagemessage',
                id: null,
                // Same key the frame URL carries on the first open, for the same reason: a path that
                // names a dozen pages at once tells the assistant nothing about which one is on screen.
                path: buildPageKey(runtime.pageQueryKeys)
            }, origin);
        } catch { /* frame not loaded yet — its URL carries the page of the mount */ }
    }

    /**
     * Hands a clicked bubble to a frame that is already standing. The frame URL is built once, on the
     * first open, so a bubble clicked after that cannot travel in it. Only ids and a flag travel; the page
     * re-resolves them into the configured text and starts the conversation exactly as the URL route does.
     * Called once the frame's circuit takes messages — straight from the click, or from the subscription
     * handshake that replays state.pendingBubble.
     * @param {{nudge: string|null, page: string|null, reply: boolean}} bubble - What the visitor clicked
     */
    function postBubbleToFrame(bubble) {
        if (!state.iframe || !state.iframe.contentWindow) {
            return;
        }

        const message = {
            type: 'feedbax:widget:bubble',
            nudge: bubble.nudge,
            page: bubble.page,
            reply: bubble.reply === true
        };

        // A campaign's id only where one was clicked: the bubbles' message stays exactly what it was.
        if (bubble.announcement) {
            message.announcement = bubble.announcement;
        }

        try {
            state.iframe.contentWindow.postMessage(message, origin);
        } catch { /* frame torn down between the check and the call — nothing left to ask */ }
    }

    /**
     * Records the bubble the visitor clicked, and gets it to the chat whichever way is open: into the URL
     * of the mount this click triggers, or to the frame that is already standing. Nothing else sets these —
     * a launcher click leaves them empty and the chat opens on its Welcome Screen.
     * The standing frame used to be reached through the page-change message alone: a generic bubble's id
     * went nowhere at all, and a page question lost the answer the visitor had just tapped and was refused
     * by any chat that was no longer pristine. A frame whose circuit is still starting cannot take the
     * message yet either, so the click waits for its subscription handshake, like every other request that
     * arrives in that gap.
     * A campaign CTA takes the same road: its id used to ride only in the frame URL as well, so a
     * campaign question clicked while the chat stood open was never asked.
     * @param {{nudge: string|null, page: string|null, reply: boolean, announcement?: string|null}} bubble
     *     What the visitor clicked
     */
    function requestBubble(bubble) {
        if (!state.iframe) {
            state.pendingNudgeId = bubble.nudge;
            state.pendingPageMessageId = bubble.page;
            state.pendingPageReply = bubble.reply;
            state.pendingAnnouncementId = bubble.announcement || null;
            return;
        }

        if (state.frameSubscribed) {
            postBubbleToFrame(bubble);
        } else {
            state.pendingBubble = bubble; // Replayed on the circuit's subscription handshake.
        }
    }

    /**
     * Takes the stack down for good: clears what is still scheduled, removes what is on screen and
     * remembers not to offer another bubble in this tab. Idempotent — dismiss, click and open all land
     * here, and two of them can happen in the same instant.
     * @param {string} [reason] - Why, for the console of a stage page (logBubble); only logged when a
     *     bubble or campaign card was actually on screen
     */
    function retireNudges(reason) {
        if (state.nudgeStack) {
            logBubble('removed: ' + (reason || 'no reason given'));
        }

        clearNudgeTimers();

        if (nudgeStackResize) {
            nudgeStackResize.disconnect();
            nudgeStackResize = null;
        }

        if (state.nudgeStack) {
            state.nudgeStack.remove();
            state.nudgeStack = null;
        }

        pageBubbleShown = false;
        announcementOnScreen = false;
        markNudgesSeen();
        trust.positionTrustCards(); // The corner is free again — the cards come back down to the launcher.
    }

    /** Cancels whatever is still scheduled — a due bubble, the opt-out offer, its confirmation. */
    function clearNudgeTimers() {
        while (nudgeTimers.length > 0) {
            clearTimeout(nudgeTimers.pop());
        }
    }

    /**
     * The close control's own path. Dismissing takes the bubble away either way; what it adds is the way
     * out for good, offered in the space the bubble just left. Without that a visitor who does not want
     * these has no answer but to close them again on the next visit, and the only lever they are left
     * with is the chat itself.
     * Falls straight back to the plain teardown when the offer would be pointless: no label from the
     * server, or a visitor who has already opted out and is only seeing this because their storage is
     * blocked.
     */
    function dismissNudges() {
        // A dismissed campaign card is a plain dismiss: the opt-out offer's words are about the chat's
        // bubbles, and standing where a campaign just left they would promise the wrong thing.
        if (!state.nudgeStack || announcementOnScreen || !options.nudgeOptOutLabel || nudgesOptedOut()) {
            retireNudges('closed by the visitor');
            return;
        }

        clearNudgeTimers();

        // Everything the visitor just dismissed goes at once, close control included: the stack stays
        // behind for the offer alone, so nothing they said no to is still standing there.
        while (state.nudgeStack.firstChild) {
            state.nudgeStack.firstChild.remove();
        }

        pageBubbleShown = false;
        markNudgesSeen();

        state.nudgeStack.appendChild(createNudgeOptOut());
        trust.positionTrustCards();
        nudgeTimers.push(setTimeout(retireNudges, optOutOfferMs));
    }

    /**
     * The opt-out chip. Deliberately quiet — smaller and paler than the bubble it replaces, because it is
     * an offer to someone who just asked for less, not another thing to sell them. One tap stores the
     * choice on this device, the chip says so, and the stack goes.
     * @returns {HTMLButtonElement}
     */
    function createNudgeOptOut() {
        const optOut = document.createElement('button');
        optOut.type = 'button';
        optOut.className = 'lw-nudge-optout';
        optOut.textContent = options.nudgeOptOutLabel;
        optOut.addEventListener('click', function () {
            optOutOfNudges();
            postNudgeOptOutToFrame(); // The widget's way back only exists once it knows the corner is off.
            clearNudgeTimers();

            // The confirmation replaces the offer in place. Without a localized one there is nothing
            // worth saying, and the stack simply leaves.
            if (options.nudgeOptOutDoneLabel) {
                optOut.textContent = options.nudgeOptOutDoneLabel;
                optOut.disabled = true;
                optOut.classList.add('lw-nudge-optout-done');
            }

            // Beside the confirmation, for a few seconds: the way out of a tap that was not meant. This
            // chip sits exactly where a visitor aims to dismiss a bubble and makes a permanent choice in
            // one go, so the undo belongs where the mistake happens rather than only in a menu they have
            // no reason to open. The stack waits for it — hence the longer timer.
            if (options.nudgeOptOutUndoLabel && state.nudgeStack) {
                state.nudgeStack.appendChild(createNudgeOptOutUndo());
                trust.positionTrustCards();
                nudgeTimers.push(setTimeout(retireNudges, optOutUndoMs));
                return;
            }

            nudgeTimers.push(setTimeout(retireNudges, optOutDoneMs));
        });

        return optOut;
    }

    /**
     * The undo beside the confirmation. Takes the standing "not again" back and leaves — deliberately
     * WITHOUT scheduling a fresh bubble: the visitor did dismiss the one that was standing there, and
     * popping it up again seconds later would answer "not that, just not never" with "here it is again".
     * The next page offers them normally.
     * @returns {HTMLButtonElement}
     */
    function createNudgeOptOutUndo() {
        const undo = document.createElement('button');
        undo.type = 'button';
        undo.className = 'lw-nudge-optout lw-nudge-optout-undo';
        undo.textContent = options.nudgeOptOutUndoLabel;
        undo.addEventListener('click', function () {
            showNudges();
            postNudgeOptOutToFrame(); // Takes the widget's menu entry away again with it.
            retireNudges('opt-out undone');
        });

        return undo;
    }

    /**
     * Tells the mounted circuit whether this visitor has switched the bubbles off for good. Same division
     * of labour as the trust corner's (see postTrustHiddenToFrame): the choice is made out here, because
     * localStorage belongs to the embedding page and the bubbles stand on it — but the way BACK belongs in
     * the widget, since the corner has no room for a settings screen and a control that only appears once
     * something is hidden has nowhere on a foreign page to appear.
     * Sent on the change and once on the circuit's handshake, so a chat opened later still knows what it
     * may offer to undo.
     */
    function postNudgeOptOutToFrame() {
        if (!state.iframe || !state.iframe.contentWindow || !state.frameSubscribed) {
            return;
        }

        try {
            state.iframe.contentWindow.postMessage(
                { type: 'feedbax:widget:nudgeoptout', optedOut: nudgesOptedOut() }, origin);
        } catch { /* frame not loaded yet — the handshake replays this */ }
    }

    /**
     * The visitor asked for the bubbles back from inside the widget. The corner is offered again in this
     * visit rather than on the next one: they asked while looking at the page it stands on, and telling
     * them to reload would be a strange answer to "wieder anzeigen".
     */
    function applyNudgeRestore() {
        if (!showNudges()) {
            return; // Nothing was switched off; nothing to schedule.
        }

        postNudgeOptOutToFrame();

        // The visit's "already offered" mark goes with it, for the reason a save broadcast clears it: a
        // visitor who just asked for the bubbles back would otherwise be told they had already had theirs.
        resetNudgesSeen();

        if (state.panelOpen) {
            // The only place this offer exists is the open chat, so this is the normal case rather than
            // the edge one — and scheduling here would hand the round straight to the timer's "already in
            // the conversation" branch, which retires the bubbles AND marks them seen. The restore would
            // quietly undo itself. The corner is owed a round instead; the panel hands it over on close.
            state.nudgesOwedAfterClose = true;
            return;
        }

        scheduleNudges(options.nudges, options.nudgeDelayMs, options.nudgeEveryPage);
    }

    /**
     * The round the open took away, now that the chat is out of the way. Called by the panel on every
     * close. Owed in two cases: the visitor asked for the bubbles back from inside the chat
     * (applyNudgeRestore), or they closed the chat without writing in this visit (Armin 2026-10-21). An
     * open retires the bubbles on its way in, but a visitor who looked and left without a word has not had
     * the conversation the bubbles were asking for, and a demo page's own buttons open the chat without the
     * visitor ever touching the launcher. A visitor who wrote is left alone: they had that conversation.
     * The usual delay applies, so the bubble does not pop up the moment the panel goes.
     */
    function scheduleNudgesAfterClose() {
        if (!state.nudgesOwedAfterClose && visitorWrote()) {
            return;
        }

        state.nudgesOwedAfterClose = false;
        // Cleared again rather than once in applyNudgeRestore: the open retired the stack on its way in,
        // and retiring marks the bubbles seen for the tab.
        resetNudgesSeen();
        scheduleNudges(options.nudges, options.nudgeDelayMs, options.nudgeEveryPage);
    }

    /** Creates the fixed stack that holds the bubbles, anchored just above the launcher. */
    function createNudgeStack() {
        const stack = document.createElement('div');
        stack.id = 'leadwin-agent-nudges';
        stack.className = 'lw-nudges'; // Everything visible comes from the shared stylesheet.
        stack.setAttribute('data-side', options.side);
        stack.dataset.theme = effectiveTheme(); // Light/dark/auto look, resolved by the stylesheet.

        // Placement stays here: only the loader pins its stack to the corner of a page it does not own.
        stack.style.position = 'fixed';
        stack.style.zIndex = '2147483646';
        // 48px launcher at 18px + a 12px gap — and above the home indicator where the phone has one, the
        // same way the launcher itself is held off it (createButton). Both have to rise together, or the
        // bottom bubble comes to rest on the button.
        stack.style.bottom = withSafeArea('78px', 'bottom');
        stack.style.alignItems = options.side === 'left' ? 'flex-start' : 'flex-end';
        applySide(stack, options.side, PANEL_EDGE_OFFSET);

        // The stack may be wider than the viewport is on a phone; the bubbles wrap instead of overflowing.
        stack.style.maxWidth = 'min(264px, calc(100vw - 36px))';

        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'lw-nudge-close';
        // This file has no dictionary of its own; the label is localized server-side with the bubbles.
        close.setAttribute('aria-label', options.nudgeCloseLabel);
        close.innerHTML = xMarkIconSvg;
        close.addEventListener('click', dismissNudges);
        stack.appendChild(close);

        document.body.appendChild(stack);

        // The height the stack ends up with is not the one it has when a bubble is appended: the
        // bubbles inherit the HOST page's font, so a face swapping in afterwards rewraps them, and an
        // answer chip or the opt-out row can arrive later still. Every one of those grows the stack
        // into the corner the trust cards are standing in — the screenshot of a bubble lying across a
        // rating card is exactly that. Measuring again on each size change is what keeps them apart;
        // repositioning is a single style write, so the observer is cheaper than the guesswork.
        if (typeof ResizeObserver === 'function') {
            nudgeStackResize = new ResizeObserver(trust.positionTrustCards);
            nudgeStackResize.observe(stack);
        }

        return stack;
    }

    /**
     * Appends one bubble. Clicking it opens the chat: a generic nudge carries its id into the frame URL
     * (its text becomes the visitor's first message), while a page question carries nothing extra — the
     * mount picks the current page's match up by itself and the agent opens with the question.
     * A page question also gets the answer offered under it, so accepting one costs a click instead of a
     * sentence. Clicking the question itself counts as that same yes wherever an answer is offered:
     * a visitor who taps "Suchst Du eine Agentur?" has answered it, and opening the chat only to make
     * them say so again is a step nobody asked for. Without an answer to send there is nothing to accept,
     * and the question opens the chat on its own. The close control is untouched by either: dismissing
     * stays the other way out.
     * @param {{id: string, text: string, reply: string|undefined, page: boolean|undefined}} nudge - Validated bubble
     */
    function showNudge(nudge) {
        if (!state.nudgeStack) {
            state.nudgeStack = createNudgeStack();
        }

        const bubble = document.createElement('button');
        bubble.type = 'button';
        bubble.className = 'lw-nudge';
        bubble.textContent = nudge.text;
        bubble.addEventListener('click', function () {
            analytics.onNudgeClick(nudge.id);
            requestBubble(nudge.page
                ? { nudge: null, page: nudge.id, reply: Boolean(nudge.reply) }
                : { nudge: nudge.id, page: null, reply: false });
            retireNudges('clicked');
            setOpen(true);
        });

        pageBubbleShown = nudge.page === true;
        state.nudgeStack.appendChild(bubble);

        // Counted where the bubble really reaches the corner, exactly like markNudgesSeen above it: a
        // visitor who left before the delay elapsed was never offered anything.
        analytics.onNudgeImpression(nudge.id);
        if (nudge.page) {
            reportPageMessageShown(nudge.id);
        }

        if (nudge.page && nudge.reply) {
            state.nudgeStack.appendChild(createNudgeReply(nudge));
        }

        trust.positionTrustCards(); // The stack just grew into the corner the trust cards were standing in.
    }

    /**
     * The answer chip under a page question — the visitor's side of it. Its own element rather than a
     * second bubble: it is what the visitor says, not what the assistant asks, and the stylesheet gives
     * it the sending side's look accordingly. It does what the question above it does (see showNudge);
     * it stays a control of its own because it shows the visitor what the tap will send.
     * @param {{id: string, reply: string}} nudge - The page question the answer belongs to
     * @returns {HTMLButtonElement}
     */
    function createNudgeReply(nudge) {
        const reply = document.createElement('button');
        reply.type = 'button';
        reply.className = 'lw-nudge-reply';
        reply.textContent = nudge.reply;
        reply.addEventListener('click', function () {
            // The same taken bubble as the question above it: tapping either is the visitor saying yes
            // to that question, and counting the two apart would split one decision into two numbers.
            analytics.onNudgeClick(nudge.id);
            requestBubble({ nudge: null, page: nudge.id, reply: true });
            retireNudges('reply clicked');
            setOpen(true);
        });

        return reply;
    }

    /* ------------------------------------------------------------------------------------------------
     * Announcements/campaigns. A campaign takes the proactive slot the bubbles use: as a card in the
     * same stack by default, or — where the contractor deliberately chose it — as one modal per page
     * view. Every text is set through textContent, like the bubbles: contractor-authored copy that
     * travels through a network payload onto someone else's website.
     * ---------------------------------------------------------------------------------------------- */

    /**
     * Presents the matched campaign and remembers that it was seen — at the moment it is actually on
     * screen, like the bubbles: a visitor who left before the delay elapsed was never shown anything.
     * @param {object} announcement - Validated campaign whose own timer just fired
     */
    function presentAnnouncement(announcement) {
        if (announcement.display === 'modal') {
            modalShownThisView = true; // One interruption per page view, and no bubble beside it.
            showAnnouncementModal(announcement);
        } else {
            showAnnouncementCard(announcement);
        }

        markAnnouncementSeen(announcement);
        analytics.onAnnouncementImpression(announcement.id);
    }

    /**
     * Fills a campaign surface with its content: the headline, the body (clamped to three lines where
     * asked, with the expand control where the server sent its labels) and the CTA button.
     * @param {HTMLElement} box - The card or the modal dialog
     * @param {object} announcement - Validated campaign
     * @param {boolean} clamped - Whether a long body starts clamped (the card does, the modal has room)
     * @param {Function} onCta - What the CTA button does in this surface
     */
    function appendAnnouncementContent(box, announcement, clamped, onCta) {
        // The minimal layout replaces the accent stripe with a small kind label — but only where the
        // server sent the visitor-language word; without it the card stays unmarked, never mislabeled.
        const kindLabel = options.announcementKindLabels
            ? options.announcementKindLabels[announcement.kind]
            : '';
        if (announcement.layout === 'minimal' && kindLabel) {
            const eyebrow = document.createElement('div');
            eyebrow.className = 'lw-announcement-eyebrow';
            eyebrow.textContent = kindLabel;
            box.appendChild(eyebrow);
        }

        // The spotlight wears the contractor's own badge in the eyebrow slot — same accent, own words.
        if (announcement.layout === 'spotlight' && announcement.badge) {
            const badge = document.createElement('div');
            badge.className = 'lw-announcement-eyebrow';
            badge.textContent = announcement.badge;
            box.appendChild(badge);
        }

        const title = document.createElement('div');
        title.className = 'lw-announcement-title';
        title.textContent = announcement.title;
        box.appendChild(title);

        if (announcement.body) {
            const body = document.createElement('div');
            body.className = 'lw-announcement-body';
            body.textContent = announcement.body;
            box.appendChild(body);

            // The expand control only exists where its labels arrived (an older server sends none);
            // without one the body simply stays unclamped — a longer card beats unreachable words.
            if (clamped && options.announcementExpandLabel) {
                body.classList.add('lw-announcement-clamp');
                box.appendChild(createAnnouncementExpand(body));
            }
        }

        // The spotlight's highlight chips; no highlights means no chip row, never an empty one.
        if (announcement.layout === 'spotlight' && announcement.highlights
            && announcement.highlights.length > 0) {
            const chips = document.createElement('div');
            chips.className = 'lw-announcement-highlights';
            for (const highlight of announcement.highlights) {
                const chip = document.createElement('span');
                chip.className = 'lw-announcement-highlight';
                chip.textContent = highlight;
                chips.appendChild(chip);
            }
            box.appendChild(chips);
        }

        if (announcement.ctaKind !== 'none' && announcement.ctaLabel) {
            const cta = document.createElement('button');
            cta.type = 'button';
            cta.className = 'lw-announcement-cta';
            cta.textContent = announcement.ctaLabel;
            cta.addEventListener('click', onCta);
            box.appendChild(cta);
        }
    }

    /**
     * The expand-in-place control under a clamped body. Expanding swaps its words for the collapse
     * label (falling back to the expand label when only that one arrived) and back again.
     * @param {HTMLElement} body - The body element the control clamps and unclamps
     * @returns {HTMLButtonElement}
     */
    function createAnnouncementExpand(body) {
        const expand = document.createElement('button');
        expand.type = 'button';
        expand.className = 'lw-announcement-expand';
        expand.textContent = options.announcementExpandLabel;
        expand.setAttribute('aria-expanded', 'false');
        expand.addEventListener('click', function () {
            const expanded = !body.classList.toggle('lw-announcement-clamp');
            expand.setAttribute('aria-expanded', String(expanded));
            expand.textContent = expanded && options.announcementCollapseLabel
                ? options.announcementCollapseLabel
                : options.announcementExpandLabel;
        });

        return expand;
    }

    /**
     * Writes the campaign's own colours onto its surface as the two custom properties every campaign
     * rule already reads. Set here rather than in a stylesheet, because they are per campaign: the
     * stripe, the eyebrow, the accent surface, the CTA and the spotlight's media gradient all follow
     * along without a single rule changing. Nothing is written where nothing arrived, so the kind's
     * hue keeps standing.
     * @param {HTMLElement} surface - The card or the modal dialog
     * @param {object} announcement - Validated campaign
     */
    function applyAnnouncementAccent(surface, announcement) {
        if (announcement.accent) {
            surface.style.setProperty('--lw-announcement-accent', announcement.accent);
        }

        if (announcement.accentDeep) {
            surface.style.setProperty('--lw-announcement-accent-deep', announcement.accentDeep);
        }
    }

    /** Puts the campaign card into the bubble stack (creating the stack, and with it the close control). */
    function showAnnouncementCard(announcement) {
        if (!state.nudgeStack) {
            state.nudgeStack = createNudgeStack();
        }

        const card = document.createElement('div');
        card.className = 'lw-announcement';
        card.dataset.kind = announcement.kind;
        card.dataset.layout = announcement.layout;
        applyAnnouncementAccent(card, announcement);

        appendAnnouncementContent(card, announcement, true, function () {
            runAnnouncementCta(announcement);
        });

        announcementOnScreen = true;
        state.nudgeStack.appendChild(card);
        trust.positionTrustCards();
    }

    /**
     * Takes the open modal down again. Focus goes back to the launcher the interruption came from —
     * but only when it actually sat inside the modal, so opening the chat does not lose its focus to
     * this cleanup.
     */
    function closeAnnouncementModal() {
        if (!state.announcementBackdrop) {
            return;
        }

        const hadFocus = state.announcementBackdrop.contains(document.activeElement);
        state.announcementBackdrop.remove();
        state.announcementBackdrop = null;

        if (hadFocus) {
            try {
                button.focus();
            } catch { /* a detached launcher takes no focus */ }
        }
    }

    /**
     * The deliberate interruption: one centered dialog over the page, opted into per campaign by the
     * contractor and hard-capped server-side to once per visitor. Escape and the backdrop close it,
     * focus lands on the way out first and cycles inside until then.
     * @param {object} announcement - Validated campaign
     */
    function showAnnouncementModal(announcement) {
        const backdrop = document.createElement('div');
        backdrop.className = 'lw-announcement-backdrop';
        backdrop.dataset.theme = effectiveTheme();
        // Placement inline like the stack's: only the loader pins itself onto a page it does not own.
        backdrop.style.cssText = 'position:fixed;inset:0;z-index:2147483646;';

        const dialog = document.createElement('div');
        dialog.className = 'lw-announcement-modal';
        dialog.dataset.kind = announcement.kind;
        dialog.dataset.layout = announcement.layout;
        dialog.dataset.size = announcement.size;
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');
        dialog.setAttribute('aria-label', announcement.title);
        applyAnnouncementAccent(dialog, announcement);

        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'lw-announcement-modal-close';
        close.setAttribute('aria-label', options.nudgeCloseLabel);
        close.innerHTML = xMarkIconSvg;
        close.addEventListener('click', closeAnnouncementModal);
        dialog.appendChild(close);

        // The spotlight splits the dialog: the words in their own column, the image panel beside them.
        // Everything else keeps writing straight into the dialog, as before.
        let contentBox = dialog;
        if (announcement.layout === 'spotlight') {
            contentBox = document.createElement('div');
            contentBox.className = 'lw-announcement-modal-content';
            dialog.appendChild(contentBox);
        }

        appendAnnouncementContent(contentBox, announcement, false, function () {
            closeAnnouncementModal();
            runAnnouncementCta(announcement);
        });

        if (announcement.layout === 'spotlight' && announcement.imageUrl) {
            appendSpotlightMedia(dialog, announcement.imageUrl);
        }

        backdrop.addEventListener('click', function (event) {
            if (event.target === backdrop) {
                closeAnnouncementModal();
            }
        });
        backdrop.addEventListener('keydown', function (event) {
            if (event.key === 'Escape') {
                closeAnnouncementModal();
                return;
            }
            trapModalFocus(event, dialog);
        });

        backdrop.appendChild(dialog);
        document.body.appendChild(backdrop);
        state.announcementBackdrop = backdrop;

        try {
            close.focus(); // First focus lands on the way out — an interruption must be easy to leave.
        } catch { /* focus is polish; the modal works without it */ }
    }

    /**
     * The spotlight's image panel on the kind's gradient, beside the dialog's words. Only its presence
     * makes the dialog split into two columns; a failed image takes the whole panel down again, so a
     * dead URL degrades to the same single-column dialog a campaign without an image renders.
     * @param {HTMLElement} dialog - The spotlight dialog the panel joins
     * @param {string} imageUrl - Validated absolute https URL of the image
     */
    function appendSpotlightMedia(dialog, imageUrl) {
        const media = document.createElement('div');
        media.className = 'lw-announcement-modal-media';

        const image = document.createElement('img');
        image.className = 'lw-announcement-modal-image';
        image.alt = ''; // Decorative: everything the campaign says stands in the text column.
        image.addEventListener('error', function () {
            media.remove();
            delete dialog.dataset.split;
        });
        image.src = imageUrl;

        media.appendChild(image);
        dialog.appendChild(media);
        dialog.dataset.split = 'true';
    }

    /**
     * The minimal focus trap an aria-modal dialog owes its keyboard visitor: Tab cycles through the
     * dialog's controls instead of wandering off into the page behind the backdrop.
     * @param {KeyboardEvent} event - The keydown that reached the backdrop
     * @param {HTMLElement} dialog - The dialog whose controls the focus cycles through
     */
    function trapModalFocus(event, dialog) {
        if (event.key !== 'Tab') {
            return;
        }

        const controls = [...dialog.querySelectorAll('button')];
        if (controls.length === 0) {
            return;
        }

        const first = controls[0];
        const last = controls.at(-1);

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    /**
     * Executes a campaign's CTA. Chat and question travel as the campaign id only — the chat resolves
     * the prewritten question and the attribution server-side. The link target was validated to https
     * at parse time and is rechecked here, defense in depth on the one path that leaves the page.
     * @param {object} announcement - Validated campaign
     */
    function runAnnouncementCta(announcement) {
        analytics.onAnnouncementClick(announcement.id);

        if (announcement.ctaKind === 'link') {
            if (typeof announcement.ctaUrl === 'string' && announcement.ctaUrl.indexOf('https://') === 0) {
                try {
                    window.open(announcement.ctaUrl, '_blank', 'noopener');
                } catch { /* a blocked popup is the host page's call */ }
            }
            retireNudges('campaign link clicked');
            return;
        }

        if (announcement.ctaKind === 'openscreen') {
            retireNudges('campaign screen opened');
            openScreen(announcement.ctaScreen);
            return;
        }

        // openchat and askquestion both carry the id into the frame: the chat opens and asks the
        // campaign's prewritten question where the CTA has one. Into the URL of the mount this click
        // triggers, or as a message to a frame that already stands, the same way a clicked bubble goes.
        requestBubble({ nudge: null, page: null, reply: false, announcement: announcement.id });
        retireNudges('campaign chat opened');
        setOpen(true);
    }

    /**
     * Schedules the configured bubble after the contractor's delay, and every campaign after its own.
     * Does nothing without bubbles, in a visit that already saw one (unless the widget offers it on every
     * page), or once the panel is open. Which bubble shows is decided when the timer fires, not when it
     * is scheduled: a page-specific question for the page the visitor is on by then beats the generic
     * nudge.
     * @param {Array<{id: string, text: string}>} nudges - Validated bubbles from the config payload
     * @param {number} delayMs - Milliseconds on the page before the bubble appears
     * @param {boolean} everyPage - Whether the visitor is offered the bubble again on every page
     */
    function scheduleNudges(nudges, delayMs, everyPage) {
        // The campaigns keep their own frequency memory, so a visit that already saw its bubble may
        // still owe one — the bubble's "already seen" gate therefore only skips the scheduling when no
        // campaign could take the slot either.
        // A page whose own question is still being written counts as a bubble that may yet happen: on a
        // site whose widget carries no site-wide bubble at all, this is the only thing that keeps a
        // timer alive long enough for the arriving question to be offered in the same visit.
        const bubblesPossible = (nudges.length > 0
                || options.pageMessages.length > 0
                || runtime.waitingForPath === buildPageKey(runtime.pageQueryKeys))
            && !nudgesAlreadySeen(everyPage);

        if (!bubblesPossible && options.announcements.length === 0) {
            logBubble(nudgesAlreadySeen(everyPage) ? 'not offered: already offered in this tab' : 'not offered: none configured');
            return;
        }

        // The visitor's standing no, checked on every scheduling path rather than once at startup: a
        // route change, a live settings update and a fresh page load all arrive here. It covers every
        // proactive surface, campaign cards included — "not again" was said to the interruption.
        if (nudgesOptedOut()) {
            logBubble('not offered: the visitor hid the bubbles for good');
            return;
        }

        // Checked here rather than once at startup: a phone that turns, a window that is dragged narrow,
        // and a live settings change all pass through this function. The switch sits in the bubbles'
        // section and speaks of the bubbles only, so the campaigns keep their timers on a narrow screen.
        if (bubblesHiddenOnThisScreen()) {
            logBubble('not offered: hidden on phones');
            scheduleAnnouncements();
            return;
        }

        // The pages the provider allowed the bubbles on. Asked per scheduling for the same reason as
        // the check above: a route change lands here too, and the answer belongs to the page the
        // visitor is on now. The campaigns are NOT gated by it — they carry their own paths, and the
        // two lists are set in two different sections of the manage panel.
        if (!urlRulesAllow(options.nudgeUrlRules)) {
            logBubble('not offered: page excluded by the page rules');
            scheduleAnnouncements();
            return;
        }

        // Every campaign waits its own number of seconds, so each gets a timer of its own rather than
        // sharing the bubbles'. Scheduled first, so a campaign due at the same moment as a bubble still
        // takes the slot — the precedence the one shared timer used to spell out in its body.
        scheduleAnnouncements();

        if (!bubblesPossible) {
            logBubble('not offered: already offered in this tab');
            return; // Only campaigns were still owed; their timers are running.
        }

        logBubble('due in ' + delayMs + ' ms');
        nudgeTimers.push(setTimeout(function offer() {
            // A visitor who opened the chat in the meantime is already in the conversation.
            if (state.panelOpen) {
                retireNudges('chat opened before it was due');
                return;
            }

            // The screen turned narrow during the delay while a campaign held the corner, so the retire
            // on that change left this timer running.
            if (bubblesHiddenOnThisScreen()) {
                logBubble('not shown: hidden on phones');
                return;
            }

            if (!proactiveSlotFree()) {
                logBubble('not shown: the corner is taken (bubble, team reply or campaign)');
                return; // A campaign was due earlier and is standing in the corner already.
            }

            // Asked again here, not only when the timer was set: between the two lies the delay the
            // provider chose, and a visitor who walked onto an excluded page during it must not be
            // handed the bubble that was scheduled on the page before.
            if (!urlRulesAllow(options.nudgeUrlRules)) {
                logBubble('not shown: page excluded by the page rules');
                return;
            }

            // This page's own question is being written right now: look again in a moment rather than
            // offering the site-wide bubble. That sentence is what a page gets when nothing could be
            // written FOR it — shown while a real question is seconds away, it teaches visitors to wave
            // away the very bubble that was about to be worth reading. The waiting ends on its own,
            // either with a question or with the server saying none is coming (see pollPageMessage).
            // "Own" on purpose: a provider waits for the provider's question even where the client's is
            // already on file, and never falls back to that one (see ownPageMessage).
            if (!ownPageMessage() && runtime.waitingForPath === buildPageKey(runtime.pageQueryKeys)) {
                nudgeTimers.push(setTimeout(offer, pageCoverageRecheckMs));
                return;
            }

            const match = ownPageMessage();

            const bubble = match
                ? { id: match.id, text: match.text, reply: match.reply, page: true }
                : nudges[0];

            if (!bubble) {
                logBubble('not shown: no question for this page and no general bubble');
                return; // Page messages configured, but none for this page and no generic nudge.
            }

            showNudge(bubble);
            logBubble('shown: ' + bubble.text);

            // Marked when the bubble is actually on screen, not when it was scheduled: a visitor who
            // left before the delay elapsed was never offered anything.
            markNudgesSeen(everyPage);
        }, delayMs));
    }

    /**
     * One timer per campaign, each at the campaign's own delay. What a timer will do is decided when it
     * fires rather than now: by then the visitor may have navigated away, opened the chat, or already
     * been given an earlier campaign.
     */
    function scheduleAnnouncements() {
        for (const announcement of options.announcements) {
            nudgeTimers.push(setTimeout(function () {
                // A visitor who opened the chat in the meantime is already in the conversation.
                if (state.panelOpen) {
                    retireNudges('chat opened before a campaign was due');
                    return;
                }

                if (proactiveSlotFree() && announcementDue(announcement)) {
                    presentAnnouncement(announcement);
                }
            }, announcement.delayMs));
        }
    }

    /**
     * Whether the corner is still free for a proactive surface. One interruption at a time, whichever
     * timer got there first: a stack on screen holds the slot, and a modal holds it for the whole page
     * view even after it was closed.
     * @returns {boolean}
     */
    function proactiveSlotFree() {
        // A waiting team reply holds the corner too (see createInboxSurface): a person's answer beats any
        // offer the widget makes on its own.
        return state.nudgeStack === null && !state.unreadCard && !modalShownThisView;
    }

    /**
     * "Every page" inside a single-page application: no document load happens, so the route change is
     * what starts the next round. A bubble still on screen from the previous route is retired first,
     * otherwise the visitor keeps an offer that belonged to a page they left.
     * Reads the current options rather than the ones present when it was hooked, so a live settings
     * change reaches the next route too.
     */
    function hookNudgeRouteChanges() {
        // The KEY the hook hands out below, not the bare pathname: seeded with a pathname, the first
        // route change of an application always looks like a new page — even one that only appended a
        // tracking parameter — and the page would be reported for coverage a second time.
        let lastPath = buildPageKey(runtime.pageQueryKeys);

        onPathChange(function (path) {
            // Ahead of the same-page test: a contains rule may name a query parameter that is no page
            // key (?tender=…), so the same page can turn into an excluded one. What stands goes; nothing
            // else changes, because for everything below it is still the same page.
            if (path === lastPath) {
                if (button.isConnected && state.nudgeStack && !urlRulesAllow(options.nudgeUrlRules)) {
                    retireNudges('page now excluded by the page rules');
                }

                return; // Same page: applications push state for filters and query strings too.
            }

            lastPath = path;

            // A new page is a new view: it may carry its own interruption again.
            modalShownThisView = false;

            // Same reason as in the message listener: an instance whose launcher left the document must
            // not schedule bubbles into a page it no longer belongs to — nor report that page for
            // coverage, which is why this guard stands ahead of the beacon rather than behind it. An
            // application that unmounts and remounts the widget would otherwise report every route once
            // per instance it ever had.
            if (!button.isConnected) {
                return;
            }

            // The visitor walked onto a page the provider excluded: what is standing in the corner was
            // offered on a page they have left, so it goes with them. Only what is on screen is taken
            // down — the branches below still run, and scheduleNudges then quietly limits itself to the
            // campaigns, which carry their own paths.
            if (state.nudgeStack && !urlRulesAllow(options.nudgeUrlRules)) {
                retireNudges('page excluded by the page rules');
            }

            // A route the served list has no question for: ask for one. Once it has been written the
            // next config request carries it, the match succeeds and nothing is sent again — which is
            // why this does not turn client-side navigation into a request per route.
            requestPageCoverageIfMissing();

            // The visitor left the page whose question they were offered, so nothing is owed any more:
            // a pending id would greet the next mount with a question about a page they walked away
            // from, and a pristine chat still showing one is cleared. The path travels either way —
            // the assistant answers about the page being read now, not the one the chat opened on.
            state.pendingPageMessageId = null;
            if (state.pendingBubble && state.pendingBubble.page) {
                state.pendingBubble = null; // Same for a click still waiting for the circuit to come up.
            }
            postPageLeftToFrame();

            if (options.nudgeEveryPage) {
                retireNudges('page changed (every page)');
                forgetNudgesSeenThisPage();
                scheduleNudges(options.nudges, options.nudgeDelayMs, true);
            } else if (pageBubbleShown || announcementOnScreen) {
                // A page question and a campaign card both belonged to the page the visitor just left;
                // the generic nudge may stay. Rescheduling gives the new page its own slot decision —
                // the retire above marked the bubbles seen, so only a campaign can still take it.
                retireNudges('page changed');
                scheduleNudges(options.nudges, options.nudgeDelayMs, false);
            }
        });
    }

    /**
     * Follows the screen while it changes: a window dragged narrow (or a phone turned upright) takes the
     * bubble away, and one dragged wide again gets the offer this visit is still owed. Only relevant for
     * widgets that stay off narrow screens at all.
     */
    function hookMobileVisibility() {
        try {
            window.matchMedia(mobileViewportQuery).addEventListener('change', function (event) {
                if (!options.nudgeHideOnMobile) {
                    return;
                }

                if (event.matches) {
                    // A campaign card stays where it is: the switch is about the bubbles. A bubble goes,
                    // and since the retire cancels every timer, the campaigns still owed are rescheduled.
                    if (!announcementOnScreen) {
                        retireNudges('window narrowed to phone width while hidden on phones');
                        scheduleNudges(options.nudges, options.nudgeDelayMs, options.nudgeEveryPage);
                    }
                } else {
                    scheduleNudges(options.nudges, options.nudgeDelayMs, options.nudgeEveryPage);
                }
            });
        } catch { /* no matchMedia: the check inside scheduleNudges is the guarantee, this is the polish */ }
    }

    /**
     * Whether the provider keeps the bubbles off this screen. Campaigns are not covered: the switch sits
     * in the bubbles' own section, and a campaign carries its own targeting.
     * @returns {boolean}
     */
    function bubblesHiddenOnThisScreen() {
        return options.nudgeHideOnMobile && isMobileViewport();
    }

    /**
     * Whether a bubble may appear on this page and screen at all: allowed by the provider's page rules and
     * not hidden on this device. What decides whether reporting the page is worth anything: a question
     * written for a page no bubble ever shows on is paid for and never read. The server checks the rules
     * again before it pays; the device is the loader's alone to know.
     * @returns {boolean}
     */
    function bubblesCanShowHere() {
        return urlRulesAllow(options.nudgeUrlRules) && !bubblesHiddenOnThisScreen();
    }

    /**
     * Live settings broadcast: the contractor saved new bubble settings while this visitor had the chat
     * open. The visit's "already offered" mark is cleared on purpose, otherwise the contractor would have
     * to hunt for a fresh tab to see what they just saved.
     * @param {object} message - The broadcast, carrying the same field names as the config payload
     */
    function applyLiveNudges(message) {
        const fresh = resolveNudgeOptions(message);

        // Same bookkeeping as applyLiveTrust: a later re-read must not retire the bubble on screen and
        // re-show a dismissed one just because the broadcast got here first.
        state.seenNudges = JSON.stringify(fresh);

        options.nudges = fresh.nudges;
        options.pageMessages = fresh.pageMessages;
        options.pageCoverage = fresh.pageCoverage;
        options.nudgeDelayMs = fresh.nudgeDelayMs;
        options.nudgeEveryPage = fresh.nudgeEveryPage;
        options.nudgeHideOnMobile = fresh.nudgeHideOnMobile;
        options.nudgeUrlRules = fresh.nudgeUrlRules;
        options.nudgeCloseLabel = fresh.nudgeCloseLabel;
        options.nudgeOptOutLabel = fresh.nudgeOptOutLabel;
        options.nudgeOptOutDoneLabel = fresh.nudgeOptOutDoneLabel;
        options.nudgeOptOutUndoLabel = fresh.nudgeOptOutUndoLabel;

        retireNudges('bubble settings changed, offering again');
        resetNudgesSeen();
        scheduleNudges(options.nudges, options.nudgeDelayMs, options.nudgeEveryPage);
    }

    return {
        requestPageCoverageIfMissing,
        retireNudges,
        closeAnnouncementModal,
        scheduleNudges,
        hookNudgeRouteChanges,
        hookMobileVisibility,
        applyLiveNudges,
        postNudgeOptOutToFrame,
        postBubbleToFrame,
        applyNudgeRestore,
        scheduleNudgesAfterClose
    };
}
